import { ReactNode, useState } from 'react';
import { Alert, Button, Dropdown, Nav, Table } from 'react-bootstrap';
import { Download } from 'react-bootstrap-icons';
import { CopyButton } from '@/components/ui/CopyButton';
import { FileExportService } from '@/utils/fileExport';
import type { ToolResultFile } from '@/types/tools';
import './ReportParts.css';

/*
 * The pieces every report tool is built from, so the four reports keep one
 * shape: the choices, the run, then the result in a table with its download.
 */

// ── The choices, once a run has started ─────────────────────────────────────

export interface ReportChoice {
  label: string;
  value: ReactNode;
}

interface ReportChoicesProps {
  choices: ReportChoice[];
  /** While the run is going the choices cannot be changed. */
  running: boolean;
  /** Back to the form with the same choices. */
  onChange: () => void;
  /** Back to the form with every choice reset. */
  onStartOver: () => void;
}

/** What this run was asked for, with the ways back to the form. */
export function ReportChoices({ choices, running, onChange, onStartOver }: ReportChoicesProps) {
  return (
    <div className="report-choices">
      <dl className="report-choices__list">
        {choices.map((choice) => (
          <div className="report-choices__item" key={choice.label}>
            <dt>{choice.label}</dt>
            <dd>{choice.value}</dd>
          </div>
        ))}
      </dl>
      {!running && (
        <div className="report-choices__actions">
          <Button size="sm" variant="outline-secondary" onClick={onChange}>
            Change choices
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={onStartOver}>
            Start over
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Downloads ───────────────────────────────────────────────────────────────

const fileKind = (file: ToolResultFile): string => {
  const ext = file.name.includes('.') ? file.name.split('.').pop() || '' : '';
  return ext ? ext.toUpperCase() : 'file';
};

interface ReportDownloadsProps {
  files: ToolResultFile[];
}

/** The download for a result. One file is one button; several open a short menu. */
export function ReportDownloads({ files }: ReportDownloadsProps) {
  if (files.length === 0) return null;

  if (files.length === 1) {
    const file = files[0];
    return (
      <Button
        size="sm"
        variant="primary"
        onClick={() => FileExportService.downloadFile(file)}
        title={`${file.name}, ${FileExportService.formatFileSize(file.size)}`}
      >
        <Download size={12} className="me-1" />
        Download {fileKind(file)}
      </Button>
    );
  }

  return (
    <Dropdown align="end">
      <Dropdown.Toggle size="sm" variant="primary">
        <Download size={12} className="me-1" />
        Download
      </Dropdown.Toggle>
      <Dropdown.Menu className="report-downloads">
        <Dropdown.Item onClick={() => FileExportService.downloadFiles(files)}>All {files.length} files</Dropdown.Item>
        <Dropdown.Divider />
        {files.map((file) => (
          <Dropdown.Item key={file.name} onClick={() => FileExportService.downloadFile(file)}>
            <span className="report-downloads__name">{file.name}</span>
            <span className="report-downloads__size">{FileExportService.formatFileSize(file.size)}</span>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

// ── Clients a run could not read ────────────────────────────────────────────

interface ReportFailuresProps {
  failures: { clientName: string; error: string }[];
}

/** The clients the run could not read, each with the reason it gave. */
export function ReportFailures({ failures }: ReportFailuresProps) {
  if (failures.length === 0) return null;
  return (
    <Alert variant="warning" className="report-failures">
      <div className="report-failures__title">
        {failures.length === 1
          ? '1 client could not be read, so it is missing from this result.'
          : `${failures.length} clients could not be read, so they are missing from this result.`}
      </div>
      <ul className="report-failures__list">
        {failures.map((failure) => (
          <li key={failure.clientName}>
            <span className="mono">{failure.clientName}</span>
            <span>{failure.error}</span>
          </li>
        ))}
      </ul>
    </Alert>
  );
}

// ── Headline figures ────────────────────────────────────────────────────────

export interface ReportStat {
  label: string;
  value: ReactNode;
}

/** The headline figures of a result. Each number carries what it counts. */
export function ReportStats({ stats }: { stats: ReportStat[] }) {
  if (stats.length === 0) return null;
  return (
    <div className="report-stats">
      {stats.map((stat) => (
        <div className="report-stat" key={stat.label}>
          <div className="report-stat__value">{stat.value}</div>
          <div className="report-stat__label">{stat.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Tabs over several tables ────────────────────────────────────────────────

export interface ReportTabItem {
  key: string;
  label: string;
  count?: number;
  /** Marks a tab that lists what went wrong. */
  danger?: boolean;
}

interface ReportTabsProps {
  tabs: ReportTabItem[];
  activeKey: string;
  onSelect: (key: string) => void;
}

/** The tables of one result. The strip scrolls sideways when there are many. */
export function ReportTabs({ tabs, activeKey, onSelect }: ReportTabsProps) {
  return (
    <div className="report-tabs">
      <Nav variant="tabs" activeKey={activeKey} onSelect={(key) => key && onSelect(key)}>
        {tabs.map((tab) => (
          <Nav.Item key={tab.key}>
            <Nav.Link eventKey={tab.key} className={tab.danger ? 'report-tabs__danger' : undefined}>
              {tab.label}
              {tab.count !== undefined && <span className="report-tabs__count">{tab.count.toLocaleString()}</span>}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
    </div>
  );
}

// ── The table ───────────────────────────────────────────────────────────────

export interface ReportColumn<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** `number` is right-aligned in even-width digits. `wrap` lets long text break. */
  kind?: 'text' | 'number' | 'wrap';
  /** Shown when the pointer rests on the heading. */
  title?: string;
  /** A class for both the heading and the cells of this column. */
  className?: string;
}

interface ReportTableProps<T> {
  columns: ReportColumn<T>[];
  rows: T[];
  /** What to say when there are no rows. */
  empty: string;
  rowClassName?: (row: T) => string | undefined;
}

/** How many rows a table draws before it asks. A very long table slows the page. */
const ROWS_SHOWN_AT_FIRST = 500;

const columnClass = <T,>(column: ReportColumn<T>): string | undefined => {
  const classes = [
    column.kind === 'number' ? 'text-end numeric' : '',
    column.kind === 'wrap' ? 'report-table__wrap' : '',
    column.className || '',
  ].filter(Boolean);
  return classes.length > 0 ? classes.join(' ') : undefined;
};

/**
 * A result table. It scrolls inside its card with the headings held in view,
 * and draws the first rows only until the person asks for all of them. The
 * download always has every row.
 */
export function ReportTable<T>({ columns, rows, empty, rowClassName }: ReportTableProps<T>) {
  const [showAll, setShowAll] = useState(false);

  const shown = showAll ? rows : rows.slice(0, ROWS_SHOWN_AT_FIRST);
  const hidden = rows.length - shown.length;

  return (
    <>
      <div className="report-table__scroll">
        <Table hover className="mb-0 report-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} className={columnClass(column)} title={column.title}>
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="report-table__empty">
                  {empty}
                </td>
              </tr>
            )}
            {shown.map((row, index) => (
              <tr key={index} className={rowClassName?.(row)}>
                {columns.map((column) => (
                  <td key={column.key} className={columnClass(column)}>
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      {hidden > 0 && (
        <div className="report-table__more">
          <span>
            Showing the first {shown.length.toLocaleString()} of {rows.length.toLocaleString()} rows. The download has
            every row.
          </span>
          <Button size="sm" variant="outline-secondary" onClick={() => setShowAll(true)}>
            Show all {rows.length.toLocaleString()} rows
          </Button>
        </div>
      )}
    </>
  );
}

// ── Cells ───────────────────────────────────────────────────────────────────

/** A long id, cut short so it does not wrap. The full value is one click away. */
export function IdCell({ value, label = 'Copy id' }: { value?: string | null; label?: string }) {
  if (!value) return null;
  const short = value.length > 14 ? `${value.slice(0, 8)}…` : value;
  return (
    <span className="report-id">
      <span className="mono" title={value}>
        {short}
      </span>
      <CopyButton value={value} label={label} size={12} />
    </span>
  );
}
